import { Component, Injector, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/Services/api.service';
import { OrganizationsService } from 'src/app/Services/organizations.service';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { StructureSelectorComponent } from 'src/app/shared/modals/StructureSelector/structureselector.component';

interface DisplayRuleSetData {
  Id: number;
  Name: string;
  TargetType: string;
}

@Component({
    selector: 'app-editsign',
    templateUrl: './editsign.component.html',
    styleUrls: ['./editsign.component.scss'],
    standalone: false
})
export class EditsignComponent extends ModelEditor implements OnInit {

  public DisplayRules: any = [];
  private searchResults: any = { PageNumber: 1, PageSize: 0, Results: null };
  public FirmwareVersions: any = [];



  private DisplayRulesInfo: Record<number, DisplayRuleSetData | false | null> = {};
  private DisplayRulesLoadingCount: number = 0;

  constructor(private apiService: ApiService, public organizationsService: OrganizationsService, private route: ActivatedRoute, private toastService: ToastrService, private modalService: SimpleModalService, private injector: Injector) {
    super("infrastructure/signconfigurations", injector);
    this.apiService.Get("infrastructure/displayrulesets/minimal").then(result => {
      if (result != null){
        this.searchResults = result;
        this.DisplayRules = this.searchResults;
      }
    });
  }

  public override DefaultModel() {
    return {};
  }
  public override AfterModelLoaded(): void {
    if(this.Model.Device != null){
      this.apiService.Get("hardware/versions/"+this.Model.Device.HardwareVersionId+"/firmware").then(result => {
        if (result != null) {
          this.FirmwareVersions = result;
        }
      });
    }

    if(this.Form.get("TargetStructures")?.value == null){
      this.Form.get("TargetStructures")?.setValue([]);
    }

    const displayRuleSetId = this.Form.get("DisplayRuleSetId")?.value;
    if (displayRuleSetId != null){
      this.DisplayRulesInfo[displayRuleSetId] = false; //placeholder to indicate loading
      this.DisplayRulesLoadingCount++;
      this.apiService.Get<any>(`infrastructure/displayrulesets/${displayRuleSetId}`).then(result => {
        this.DisplayRulesInfo[displayRuleSetId] = result;
        this.DisplayRulesLoadingCount--;
      }).catch(() => {
        this.DisplayRulesLoadingCount--;
        this.toastService.error("Failed to load selected display rule set.");
        this.DisplayRulesInfo[displayRuleSetId] = null;
      });

    }
  }

  public AddParkingStructure(){
    this.modalService.addModal(StructureSelectorComponent,
      { Structures : this.Form.get('TargetStructures')?.value})
      .subscribe((result: any) => {
        if (result != null) {
          var fa = this.FormArray(this.Form, "TargetStructures");
          fa.clear();
          result.forEach((value : any) => {
            var control = new FormControl(value);
            fa.push(control);
          })
          fa.markAsTouched();
          this.Form.markAsDirty();

          const displayRuleSetId = this.Form.get("DisplayRuleSetId")?.value;
          if (displayRuleSetId != null) {
            const DisplayRuleSet = this.DisplayRulesInfo[displayRuleSetId];
            if (DisplayRuleSet == null || DisplayRuleSet == false) {
              this.toastService.warning("The selected display rule set is still loading. Please wait for it to finish loading before saving.");
              return;
            } else if (!this.checkStructureCompatibility(DisplayRuleSet.TargetType)) {
              this.toastService.warning(`The selected structures are incompatible with the new display rule set target type ${DisplayRuleSet.TargetType}. The Display Rule Set has been cleared.`);
              this.Form.get("DisplayRuleSetId")?.setValue(null);
              this.Form.markAsDirty();
            }

          }

          const overrideDisplayRuleSetId = this.Form.get("OverrideDisplayRuleSetId")?.value;
          if (overrideDisplayRuleSetId != null) {
            const OverrideDisplayRuleSet = this.DisplayRulesInfo[overrideDisplayRuleSetId];
            if (OverrideDisplayRuleSet == null || OverrideDisplayRuleSet == false) {
              this.toastService.warning("The selected override display rule set is still loading. Please wait for it to finish loading before saving.");

            } else if (!this.checkStructureCompatibility(OverrideDisplayRuleSet.TargetType)) {
              this.toastService.warning(`The selected structures are incompatible with the new display rule set target type ${OverrideDisplayRuleSet.TargetType}. The Override display rule Set has been cleared.`);
              this.Form.get("OverrideDisplayRuleSetId")?.setValue(null);
              this.Form.markAsDirty();
            }
          }
        }
      });
  }

  private checkStructureCompatibility(structure: string): boolean {
    const structures = this.Form.get('TargetStructures')?.value;
    if (structures == null || structures.length == 0) {
      return true;
    }

    if (structures.length == 1) {
      return structure.toLowerCase() == structures[0].StructureType.replace(/\s/g, "").toLowerCase();
    }
    return structure.toLowerCase() == "parkingstructuregroup";
  }

  private displayRuleSetRequests = new Map<number, Promise<DisplayRuleSetData | false>>();

  private async LoadRuleSet(value: string): Promise<DisplayRuleSetData | false> {
    if (value == null || value == "") {
      return false;
    }
    if (!Number.isFinite(Number(value))) {
      value = value.split(':')[1].trim();
      if(value == ""){
        return false;
      }
    }
    var selectedId = parseInt(value);

    const DisplayRuleSet = this.DisplayRulesInfo[selectedId];
    if (DisplayRuleSet != null){
      return DisplayRuleSet;
    }

    const existingRequest = this.displayRuleSetRequests.get(selectedId);
    if (existingRequest) {
      return await existingRequest;
    }

    this.DisplayRulesLoadingCount++;
    const requestPromise = (async () => {
      try {
        const result = await this.apiService.Get<any>(
          `infrastructure/displayrulesets/${selectedId}`
        );
        this.DisplayRulesInfo[selectedId] = result;
        return result;
      } catch (e) {
        this.toastService.error("Failed to load selected display rule set.");
        this.DisplayRulesInfo[selectedId] = null;
        return false;
      } finally {
        this.DisplayRulesLoadingCount--;
        this.displayRuleSetRequests.delete(selectedId);
      }
    })();

    this.displayRuleSetRequests.set(selectedId, requestPromise);
    return await requestPromise;
  }

  public async SelectRuleSet(event: any, isOverride: boolean) {
    const displayRuleSet = await this.LoadRuleSet(event.target.value);

    if (displayRuleSet == false || displayRuleSet == null){
      return;
    }

    if (!this.checkStructureCompatibility(displayRuleSet.TargetType)) {
      this.toastService.warning(`The selected structures are incompatible with the new display rule set target type ${displayRuleSet.TargetType}. Please re-select appropriate structures.`);
      var fa = this.FormArray(this.Form, "TargetStructures");
      fa.clear();
      this.Form.markAsDirty();
    }

    var otherName;
    if (isOverride) {
      otherName = 'DisplayRuleSetId';
    } else {
      otherName = 'OverrideDisplayRuleSetId';
    }
    var other = await this.LoadRuleSet(this.Form.get(otherName)?.value);
    if (other == false || other == null) {
      return;
    }
    if (other.TargetType != displayRuleSet.TargetType) {
      this.toastService.warning(`The selected display rule sets are not compatible, the one you selected is for ${displayRuleSet.TargetType} while the other one is for ${other.TargetType}.`);
      this.Form.get(otherName)?.setValue(null);
      this.Form.get(otherName)?.markAsDirty();
      this.Form.get(otherName)?.markAsTouched();
      this.Form.markAsDirty();
    }

  }


  public override BeforeSave(): boolean | void {
    if (this.DisplayRulesLoadingCount > 0){
      this.toastService.error("Please wait for all display rules to finish loading before saving.");
      return false;
    }

    const structures = this.Form.get('TargetStructures')?.value;
    if (structures == null || structures.length == 0) {
      this.toastService.warning("Please select at least one target structure.");
      return false;
    }

    const displayRuleSetId = this.Form.get("DisplayRuleSetId")?.value;
    if (displayRuleSetId != null){
      const DisplayRuleSet = this.DisplayRulesInfo[displayRuleSetId];
      if (DisplayRuleSet == null || DisplayRuleSet == false) {
        this.toastService.warning("The selected display rule set is still loading. Please wait for it to finish loading before saving.");
        return false;
      }

      if (!this.checkStructureCompatibility(DisplayRuleSet.TargetType)){
        this.toastService.warning(`The selected structures are incompatible with the new display rule set target type ${DisplayRuleSet.TargetType}.`);
        return false;
      }

    }

    const overrideDisplayRuleSetId = this.Form.get("OverrideDisplayRuleSetId")?.value;
    if (overrideDisplayRuleSetId != null && this.DisplayRulesInfo[overrideDisplayRuleSetId] != null){
      const OverrideDisplayRuleSet = this.DisplayRulesInfo[overrideDisplayRuleSetId];
      if (OverrideDisplayRuleSet == null || OverrideDisplayRuleSet == false) {
        this.toastService.warning("The selected override display rule set is still loading. Please wait for it to finish loading before saving.");
        return false;
      }

      if (!this.checkStructureCompatibility(OverrideDisplayRuleSet.TargetType)){
        this.toastService.warning(`The selected structures are incompatible with the new display rule set target type ${OverrideDisplayRuleSet.TargetType}.`);
        return false;
      }
    }


  }

  public override Validators() {
  }

}
